// ============================================================
// SearchParams: typed class with defaults + sparse serializer
// ============================================================

export interface RangeParam {
    min: number;
    max: number;
}

interface ListingIndex {
    listingIndexId: number;
    advancedSearchQuery: string;
    // vectorSearch removed from here
}

interface SearchWithinMap {
    enabled: boolean;
    latitude: RangeParam;
    longitude: RangeParam;
}

interface NearLocation {
    enabled: boolean;
    address: string;
    latitude: RangeParam;
    longitude: RangeParam;
    distanceInMiles: number;
}

export class SearchParams {
    aiMode: boolean = false;
    querySummary: string = "";
    sort: string = "newest";
    useScoring: boolean = false;
    offset: number = 0;
    limit: number = 200;
    idx_site_id: number = 0;
    countOnly: boolean = false;
    returnDetail: boolean = true;

    // string filters - default empty means "not set"
    city: string = "";
    county: string = "";
    subdivision: string = "";
    postalCode: string = "";
    address: string = "";
    building: string = "";
    excludeRemarks: string = "";

    // Vector search at root level (was per-listingIndex)
    vectorSearch: string = "";
    negativeVectorSearch: string = "";

    // boolean filters
    openHouses: boolean = false;

    // display settings
    showListings: number = 0;  // 0=all, 1=office, 2=agent
    layout: number = 0;          // 0=list, 1=detail, 2=map

    // range filters - null means "not set"
    price: RangeParam | null = null;
    beds: RangeParam | null = null;
    baths: RangeParam | null = null;
    sqft: RangeParam | null = null;
    acreage: RangeParam | null = null;
    yearBuilt: RangeParam | null = null;
    timestamp: RangeParam | null = null;

    // complex filters
    polygons: number[][] | null = null;
    mlsNumbers: string[] | null = null;
    searchWithinMap: SearchWithinMap | null = null;
    nearLocation: NearLocation | null = null;

    // listing indices & weights
    fieldWeights: Record<string, any> = {};
    listingIndices: ListingIndex[] = [];

    // ---- defaults for comparison during serialization ----
    private static readonly DEFAULTS: Record<string, any> = {
        sort: "newest",
        querySummary:"",
        aiMode:false,
        useScoring: false,
        offset: 0,
        limit: 200,
        idx_site_id: 0,
        countOnly: false,
        returnDetail: true,
        city: "",
        county: "",
        subdivision: "",
        postalCode: "",
        address: "",
        building: "",
        excludeRemarks: "",
        vectorSearch: "",
        negativeVectorSearch: "",
        openHouses: false,
        showListings: 0,
        layout: 0,
        price: null,
        beds: null,
        baths: null,
        sqft: null,
        acreage: null,
        yearBuilt: null,
        timestamp: null,
        polygons: null,
        mlsNumbers: null,
        searchWithinMap: null,
        nearLocation: null,
        fieldWeights: {},
        listingIndices: [],
    };

    /**
     * Serialize to a plain object, omitting any value that matches its default.
     * Always includes idx_site_id and listingIndices since those are required by the backend.
     */
    toJSON(): any {
        const alwaysInclude = new Set(["idx_site_id", "listingIndices"]);
        const out: any = {};
        for (const key of Object.keys(SearchParams.DEFAULTS)) {
            const val = (this as any)[key];
            if (alwaysInclude.has(key)) {
                out[key] = val;
                continue;
            }
            const def = SearchParams.DEFAULTS[key];
            if (!SearchParams.deepEqual(val, def)) {
                out[key] = val;
            }
        }

        if (this.aiMode) {
            out.aiMode = true;
        }
        return out;
    }

    /**
     * Populate this instance from a plain JSON object (sparse — missing keys keep defaults).
     */
    static fromJSON(js: any): SearchParams {
        const sp = new SearchParams();
        if (!js || typeof js !== "object") return sp;
        for (const key of Object.keys(SearchParams.DEFAULTS)) {
            if (key in js) {
                (sp as any)[key] = js[key];
            }
        }
        if (js.aiMode === true) {
            sp.aiMode = true;
        }
        return sp;
    }

    private static deepEqual(a: any, b: any): boolean {
        if (a === b) return true;
        if (a == null || b == null) return a === b;
        if (typeof a !== typeof b) return false;
        if (typeof a !== "object") return false;
        if (Array.isArray(a) !== Array.isArray(b)) return false;
        if (Array.isArray(a)) {
            if (a.length !== b.length) return false;
            return a.every((v: any, i: number) => SearchParams.deepEqual(v, b[i]));
        }
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        return keysA.every(k => SearchParams.deepEqual(a[k], b[k]));
    }
}